import React from "react";

export function PageHeader({ title, subtitle, kicker, actions, testId }) {
  return (
    <div className="flex items-start justify-between gap-6 mb-8" data-testid={testId || "page-header"}>
      <div>
        {kicker && <div className="overline mb-2">{kicker}</div>}
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-[color:var(--hl-text)] tracking-tight leading-tight">
          {title}
        </h1>
        {subtitle && <p className="mt-2 text-slate-500 max-w-2xl text-sm">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function KPITile({ label, value, sub, tone = "light", accent, testId }) {
  const dark = tone === "dark";
  return (
    <div
      className={`hl-card p-5 flex flex-col gap-2 ${dark ? "hl-kpi-dark" : ""}`}
      data-testid={testId}
    >
      <div className={`overline ${dark ? "text-white/60" : ""}`}>{label}</div>
      <div className={`font-display font-bold tracking-tight text-3xl ${dark ? "text-white" : "text-[color:var(--hl-text)]"}`}>
        {value}
      </div>
      {sub && (
        <div className={`text-xs ${dark ? "text-white/70" : "text-slate-500"}`}>{sub}</div>
      )}
      {accent && (
        <div className="text-xs font-semibold" style={{ color: "var(--hl-orange)" }}>
          {accent}
        </div>
      )}
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    paid: "status-success", present: "status-success", positive: "status-success",
    accepted: "status-success", enrolled: "status-success", returned: "status-success",
    resolved: "status-success",
    part_paid: "status-warning", late: "status-warning", minor: "status-warning",
    review: "status-warning", interview: "status-warning", offer: "status-warning",
    issued: "status-info", application: "status-info", active: "status-info",
    overdue: "status-danger", absent: "status-danger", major: "status-danger",
    rejected: "status-danger",
    excused: "status-muted",
  };
  const cls = map[status] || "status-muted";
  return (
    <span className={`status-pill ${cls}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Markdown-lite renderer
// ---------------------------------------------------------------------------
// A controlled, safe subset renderer that eliminates raw `**bold**`, `##` and
// `- bullet` leakage across the executive briefing surfaces (Command Deck,
// Executive Intelligence, AI Executive, Board Pack, Academic360). No external
// dependency; the input is always our own generated briefing, never arbitrary
// user HTML. If the input contains no markdown markers, it renders as a
// pre-wrapped paragraph identical to the previous behaviour.
export function Markdown({ children, className = "" }) {
  if (children == null || children === "") return null;
  const text = String(children).replace(/\r\n/g, "\n").trim();

  // Split into blocks separated by blank lines.
  const blocks = text.split(/\n{2,}/);

  const renderInline = (line, key) => {
    // Escape < to prevent HTML injection, then apply **bold** replacement.
    const safe = line.replace(/</g, "&lt;");
    const html = safe.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    return <span key={key} dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <div className={`markdown-lite space-y-2 ${className}`}>
      {blocks.map((block, bi) => {
        const lines = block.split("\n");
        // H2/H3
        if (/^#{2,3}\s+/.test(lines[0])) {
          const level = lines[0].match(/^(#{2,3})/)[1].length;
          const title = lines[0].replace(/^#{2,3}\s+/, "");
          const rest = lines.slice(1);
          const H = level === 2 ? "h4" : "h5";
          return (
            <div key={bi}>
              <H className="font-display font-semibold text-[color:var(--hl-navy)] text-[13px] uppercase tracking-[0.14em] mb-1">
                {renderInline(title, `h-${bi}`)}
              </H>
              {rest.length > 0 && (
                <div className="text-sm text-slate-700 leading-relaxed">
                  {rest.map((l, li) => (<div key={li}>{renderInline(l, `rl-${bi}-${li}`)}</div>))}
                </div>
              )}
            </div>
          );
        }
        // Bulleted list
        if (lines.every((l) => /^\s*[-*]\s+/.test(l))) {
          return (
            <ul key={bi} className="list-disc pl-5 text-sm text-slate-700 leading-relaxed space-y-1">
              {lines.map((l, li) => (
                <li key={li}>{renderInline(l.replace(/^\s*[-*]\s+/, ""), `li-${bi}-${li}`)}</li>
              ))}
            </ul>
          );
        }
        // Bold-only headline line (a line surrounded by ** on both sides is treated as sub-header)
        if (lines.length === 1 && /^\*\*[^*]+\*\*:/.test(lines[0])) {
          const [, head, tail = ""] = lines[0].match(/^\*\*([^*]+)\*\*:\s*(.*)$/) || [];
          return (
            <p key={bi} className="text-sm leading-relaxed text-slate-700">
              <span className="font-semibold text-[color:var(--hl-navy)]">{head}</span>
              {tail ? ` — ${tail}` : null}
            </p>
          );
        }
        // Paragraph fallback (may contain inline **bold**)
        return (
          <p key={bi} className="text-sm text-slate-700 leading-relaxed">
            {lines.map((l, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {renderInline(l, `p-${bi}-${li}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

export function AIInsightCard({ title, body, severity = "info", testId }) {
  return (
    <div className="hl-card hl-ai-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--hl-orange)] font-bold">
          AI Executive Intelligence
        </span>
        <span className="ml-auto text-[10px] text-slate-500 uppercase tracking-widest">{severity}</span>
      </div>
      <h3 className="font-display font-bold text-lg tracking-tight text-[color:var(--hl-navy)] mb-2">{title}</h3>
      <Markdown>{body}</Markdown>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, body, testId }) {
  return (
    <div className="hl-card p-10 text-center" data-testid={testId || "empty-state"}>
      {Icon && <Icon className="w-8 h-8 mx-auto text-slate-300 mb-3" />}
      <div className="font-semibold text-slate-700">{title}</div>
      {body && <div className="text-sm text-slate-500 mt-1">{body}</div>}
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="text-sm text-slate-500 py-8" data-testid="loading">
      {label}
    </div>
  );
}

export function Section({ title, subtitle, actions, children, testId }) {
  return (
    <section className="mb-8" data-testid={testId}>
      {(title || actions) && (
        <div className="flex items-end justify-between mb-4">
          <div>
            {title && (
              <h2 className="font-display font-bold text-xl tracking-tight text-slate-800">
                {title}
              </h2>
            )}
            {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function DataTable({ columns, rows, empty = "No records", testId, dense = false }) {
  if (!rows || rows.length === 0) {
    return <EmptyState title={empty} testId={testId && `${testId}-empty`} />;
  }
  return (
    <div className="hl-card overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto">
        <table className={`hl-table ${dense ? "hl-table--dense" : ""}`}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key || c.label}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i}>
                {columns.map((c) => (
                  <td key={c.key || c.label}>
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
